import React, { useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LottiiePalyer from "../../../Loading/LottiiePalyer";

const UploadFileForm = ({ path, onSubmit }) => {
    const { t } = useTranslation('common');
    const [title, setTitle] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [errorState, setErrorState] = useState("");
    const [errorList, setErrorList] = useState([]);
    const [showProcessing, setShowProcessing] = useState(false);
    const toastIdRef = useRef(null);

    const onFileChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const name = file.name;
            let splitName = name.split('.');
            let ext = splitName[splitName.length - 1];
            if (ext) {
                const baseName = splitName.slice(0, splitName.length - 1).join('.');
                setSelectedFile(file);
                setFileName(name);
                setErrorState("text-success");
                setTitle(prev => prev ? prev : baseName);
            } else {
                setFileName(t('Pages.FileManager.Form.File.error'));
                setErrorState('text-danger');
                setSelectedFile(null);
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        toastIdRef.current = toast(t('Processing'), {
            position: "bottom-left",
            autoClose: false
        });
        toast.update(toastIdRef.current, {
            render: () => <div> <i className="fa-spin fas fa-spinner "></i> {t('Processing')} </div>,
            autoClose: false,
            closeOnClick: false,
        });

        const trimmedTitle = title.trim();
        const errors = {};
        if (!selectedFile) {
            errors.File = 'Pages.FileManager.Form.File.error';
        }
        if (!trimmedTitle) {
            errors.Title = 'Pages.FileManager.Form.Title.error';
        }

        if (Object.keys(errors).length > 0) {
            let list = [];
            for (const [key, value] of Object.entries(errors)) {
                list.push(<li key={`error-${key}`}>{t(value)}.</li>);
            }
            setErrorList(list);

            toast.update(toastIdRef.current, {
                render: () => (
                    <div>
                        <div className="h5">
                            <i className="icon fal fa-bug"></i>
                            {t('Pages.Register.error_AllInformationIsRequired_toast.Title')}
                        </div>
                        <div className="h6">
                            {t('Pages.Register.error_AllInformationIsRequired_toast.Body')}
                        </div>
                    </div>
                ),
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        } else {
            const formData = new FormData();
            formData.append("myFile", selectedFile);
            formData.append("Title", trimmedTitle);
            formData.append("path", path || "");

            setErrorList([]);
            setShowProcessing(true);
            onSubmit(formData, toastIdRef.current);
        }
    };

    return (
        <>
            {showProcessing ? (
                <div className="text-center py-4">
                    <div className="title h4 mb-3">
                        <i className="fa-spin fas fa-spinner mr-2"></i> {t('Processing')}
                    </div>
                    <LottiiePalyer />
                </div>
            ) : (
                <form method="POST" encType="multipart/form-data" className="contact-form" onSubmit={handleSubmit} autoComplete="new-password">
                    {errorList.length > 0 && (
                        <div className="alert alert-danger mb-4 p-3 w-100" role="alert">
                            <h5 className="alert-heading">{t('ErrorsList')}</h5>
                            <div className="border-bottom border-white opacity-20 mb-2"></div>
                            <ul className="list-unstyled mb-0">
                                {errorList}
                            </ul>
                        </div>
                    )}

                    <div className="form-group w-100 mb-3">
                        <label className='text-dark font-size-h5 mb-2'>
                            {t('Pages.FileManager.Form.Title.title')}{' '}
                            <i title={t('required')} className="fas fa-star-of-life font-size-10 text-danger"></i>
                        </label>
                        <input
                            name="Title"
                            type="text"
                            required={true}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t('Pages.FileManager.Form.Title.Placeholder')}
                            title={t('Pages.FileManager.Form.Title.title')}
                            className="form-control"
                            autoComplete="off"
                            spellCheck={false}
                        />
                    </div>

                    <div className="form-group w-100 pos-rel mb-4" style={{ height: '200px' }}>
                        <label className='text-dark font-size-h5 text-right w-100 mb-2'>
                            {t('Pages.FileManager.Form.File.title')}{' '}
                            <i title={t('required')} className="fas fa-star-of-life font-size-10 text-danger"></i>
                        </label>
                        <div id="ExcelFileContainer" className="d-flex flex-column align-items-center justify-content-center border border-dashed rounded p-4 text-center bg-light" style={{ cursor: 'pointer', height: '150px' }}>
                            <i className="fad fa-cloud-upload text-success fa-3x mb-2"></i>
                            <span className="text-secondary font-weight-medium">
                                {fileName || t('Pages.FileManager.Form.File.Placeholder')}
                            </span>
                        </div>
                        <input
                            id="ExcelFile"
                            name="File"
                            onChange={onFileChange}
                            type="file"
                            required={true}
                            className="form-control"
                            style={{
                                position: 'absolute',
                                top: '32px',
                                left: 0,
                                width: '100%',
                                height: '150px',
                                opacity: 0,
                                cursor: 'pointer'
                            }}
                        />
                    </div>

                    {fileName && (
                        <div className="w-100 text-center mb-3">
                            <label className={`font-size-h5 ${errorState}`}>
                                {fileName}
                            </label>
                        </div>
                    )}

                    <div className="form-group w-100 mt-4">
                        <button type="submit" className="custom-button w-100">
                            <span><i className="fal fa-upload mr-2"></i> {t('Pages.FileManager.Form.Upload')}</span>
                        </button>
                    </div>
                </form>
            )}
        </>
    );
};

export default UploadFileForm;
